import { toIso, parseIso, today, addDays, addMonths, startOfMonth, firstDayOfWeek, isoWeek, formatDate } from '../dates.js';

const CHEVRON_LEFT = '<svg class="mellow-icon" data-variant="micro" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M9.78 4.22a.75.75 0 0 1 0 1.06L7.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L5.47 8.53a.75.75 0 0 1 0-1.06l3.25-3.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd"/></svg>';
const CHEVRON_RIGHT = '<svg class="mellow-icon" data-variant="micro" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/></svg>';

let instances = 0;

/** Escapes text for the HTML template below (ids and translations are not ours to trust). */
const esc = (value) => String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

/**
 * <mellow-calendar>: a localized month grid (WAI-ARIA date grid).
 *
 * mode:  single (value "YYYY-MM-DD") | multiple (array) | range ({ start, end })
 * attrs: min, max, unavailable ("2026-10-03,2026-12-25"), week-numbers, with-today,
 *        fixed-weeks, start-day (0–6), locale, selectable-header (month/year dropdowns)
 *
 * `.value` + `input`/`change` events (value as detail) make wire:model work.
 */
export class MellowCalendar extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        // A value set before the element connected (e.g. by wire:model) wins over the attribute.
        this.selected ??= this.parseValue(this.getAttribute('value'));
        const initial = this.selectedDates()[0] ?? today();
        this.month = startOfMonth(this.clamp(initial));
        this.focusDate = this.clamp(initial);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('pointerover', this.onHover);
        this.addEventListener('pointerleave', () => this.hover(null));

        this.render();
    }

    static get observedAttributes() {
        return ['min', 'max', 'unavailable', 'locale'];
    }

    attributeChangedCallback() {
        if (this._ready) this.render();
    }

    get mode() {
        return this.getAttribute('mode') ?? 'single';
    }

    get locale() {
        return this.getAttribute('locale') || window.Mellow?.config.locale || document.documentElement.lang || 'en';
    }

    get t() {
        return window.Mellow?.t ?? ((key) => key);
    }

    /* ── Value ────────────────────────────────────────────────── */

    parseValue(raw) {
        let value = raw;
        if (typeof raw === 'string' && /^[[{]/.test(raw.trim())) {
            try {
                value = JSON.parse(raw);
            } catch {}
        }

        if (this.mode === 'range') {
            return { start: value?.start || null, end: value?.end || null };
        }
        if (this.mode === 'multiple') {
            return Array.isArray(value) ? value.filter(Boolean).map(String) : value ? [String(value)] : [];
        }
        return typeof value === 'string' && value ? value.slice(0, 10) : null;
    }

    get value() {
        if (this.mode === 'range') return { start: this.selected.start, end: this.selected.end };
        if (this.mode === 'multiple') return [...this.selected];
        return this.selected ?? '';
    }

    set value(value) {
        this.selected = this.parseValue(value);
        const first = this.selectedDates()[0];
        if (first && !this.isVisible(first)) {
            this.month = startOfMonth(first);
            this.focusDate = first;
        }
        if (this._ready) this.render();
    }

    selectedDates() {
        if (this.mode === 'range') return [this.selected.start, this.selected.end].filter(Boolean).map(parseIso);
        if (this.mode === 'multiple') return this.selected.map(parseIso).filter(Boolean);
        return this.selected ? [parseIso(this.selected)] : [];
    }

    emit() {
        const value = this.value;
        this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: value }));
        this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: value }));
    }

    /* ── Constraints ──────────────────────────────────────────── */

    isDisabled(date) {
        const iso = toIso(date);
        const min = this.getAttribute('min');
        const max = this.getAttribute('max');
        const unavailable = (this.getAttribute('unavailable') ?? '').split(',').map((d) => d.trim());

        return (min && iso < min) || (max && iso > max) || unavailable.includes(iso);
    }

    clamp(date) {
        const min = parseIso(this.getAttribute('min'));
        const max = parseIso(this.getAttribute('max'));
        if (min && date < min) return min;
        if (max && date > max) return max;
        return date;
    }

    isVisible(date) {
        return date.getFullYear() === this.month.getFullYear() && date.getMonth() === this.month.getMonth();
    }

    /* ── Selection ────────────────────────────────────────────── */

    select(date) {
        if (this.isDisabled(date)) return;
        const iso = toIso(date);

        if (this.mode === 'range') {
            const { start, end } = this.selected;
            if (!start || end) {
                this.selected = { start: iso, end: null };
            } else {
                this.selected = iso < start ? { start: iso, end: start } : { start, end: iso };
            }
        } else if (this.mode === 'multiple') {
            this.selected = this.selected.includes(iso)
                ? this.selected.filter((d) => d !== iso)
                : [...this.selected, iso].sort();
        } else {
            this.selected = iso;
        }

        this.focusDate = date;
        this.render();
        this.emit();

        const complete = this.mode !== 'range' || this.selected.end;
        this.dispatchEvent(new CustomEvent('mellow:select', { bubbles: true, detail: { value: this.value, complete } }));
    }

    isSelected(iso) {
        if (this.mode === 'range') return iso === this.selected.start || iso === this.selected.end;
        if (this.mode === 'multiple') return this.selected.includes(iso);
        return iso === this.selected;
    }

    inRange(iso) {
        if (this.mode !== 'range') return false;
        const start = this.selected.start;
        const end = this.selected.end ?? this._hover;
        if (!start || !end) return false;
        const [from, to] = start < end ? [start, end] : [end, start];
        return iso > from && iso < to;
    }

    hover(iso) {
        if (this.mode !== 'range' || !this.selected.start || this.selected.end || this._hover === iso) return;
        this._hover = iso;
        this.querySelectorAll('[data-date]').forEach((button) => button.toggleAttribute('data-in-range', this.inRange(button.dataset.date)));
    }

    /* ── Navigation ───────────────────────────────────────────── */

    moveFocus(date) {
        const target = this.clamp(date);
        if (!this.isVisible(target)) this.month = startOfMonth(target);
        this.focusDate = target;
        this.render();
        this.querySelector(`[data-date="${toIso(target)}"]`)?.focus();
    }

    showMonth(offset) {
        // Re-rendering replaces the buttons: keep focus on the navigation button that was used.
        const nav = document.activeElement?.closest?.('[data-nav]')?.dataset.nav;

        this.month = addMonths(this.month, offset);
        this.focusDate = this.clamp(addMonths(this.focusDate, offset));
        if (!this.isVisible(this.focusDate)) this.focusDate = this.month;
        this.render();

        if (nav) this.querySelector(`[data-nav="${nav}"]:not(:disabled)`)?.focus();
    }

    /** Jump to a month picked in the header dropdowns, keeping the day of the month where possible. */
    showMonthOf(year, month) {
        const target = this.clamp(new Date(year, month, 1, 12));
        this.month = startOfMonth(target);
        const days = new Date(this.month.getFullYear(), this.month.getMonth() + 1, 0).getDate();
        this.focusDate = this.clamp(new Date(this.month.getFullYear(), this.month.getMonth(), Math.min(this.focusDate.getDate(), days), 12));
        this.render();
    }

    /** Years offered in the header: the min/max range, or 100 years back and 10 ahead. */
    yearRange() {
        const current = today().getFullYear();
        const shown = this.month.getFullYear();
        const from = parseIso(this.getAttribute('min'))?.getFullYear() ?? Math.min(current - 100, shown);
        const to = parseIso(this.getAttribute('max'))?.getFullYear() ?? Math.max(current + 10, shown);
        return Array.from({ length: to - from + 1 }, (_, i) => from + i);
    }

    headerSelects(locale) {
        const year = this.month.getFullYear();
        const min = this.getAttribute('min');
        const max = this.getAttribute('max');
        const monthName = new Intl.DateTimeFormat(locale, { month: 'long' });

        let months = '';
        for (let m = 0; m < 12; m++) {
            const first = toIso(new Date(year, m, 1, 12));
            const last = toIso(new Date(year, m + 1, 0, 12));
            const disabled = (min && last < min) || (max && first > max);
            months += `<option value="${m}"${m === this.month.getMonth() ? ' selected' : ''}${disabled ? ' disabled' : ''}>${esc(monthName.format(new Date(year, m, 1, 12)))}</option>`;
        }

        const years = this.yearRange().map((y) => `<option value="${y}"${y === year ? ' selected' : ''}>${y}</option>`).join('');

        return `<div class="mellow-calendar-selects">
                    <select class="mellow-calendar-select" data-select="month" aria-label="${esc(this.t('calendar.month'))}">${months}</select>
                    <select class="mellow-calendar-select" data-select="year" aria-label="${esc(this.t('calendar.year'))}">${years}</select>
                </div>`;
    }

    /** Focus the currently focusable day (e.g. when a date picker opens). */
    focusDay() {
        this.querySelector('[data-date][tabindex="0"]')?.focus();
    }

    onClick = (event) => {
        const day = event.target.closest('[data-date]');
        if (day) return this.select(parseIso(day.dataset.date));

        const nav = event.target.closest('[data-nav]');
        if (nav) return this.showMonth(Number(nav.dataset.nav));

        if (event.target.closest('[data-today]')) this.moveFocus(today());
    };

    onHover = (event) => {
        const day = event.target.closest('[data-date]');
        if (day) this.hover(day.dataset.date);
    };

    onKeydown = (event) => {
        const day = event.target.closest('[data-date]');
        if (!day) return;

        const date = parseIso(day.dataset.date);
        const rtl = getComputedStyle(this).direction === 'rtl';
        const weekStart = this.startDay;
        const weekday = (date.getDay() - weekStart + 7) % 7;

        const targets = {
            ArrowLeft: addDays(date, rtl ? 1 : -1),
            ArrowRight: addDays(date, rtl ? -1 : 1),
            ArrowUp: addDays(date, -7),
            ArrowDown: addDays(date, 7),
            Home: addDays(date, -weekday),
            End: addDays(date, 6 - weekday),
            PageUp: addMonths(date, event.shiftKey ? -12 : -1),
            PageDown: addMonths(date, event.shiftKey ? 12 : 1),
        };

        if (targets[event.key]) {
            event.preventDefault();
            this.moveFocus(targets[event.key]);
            this.hover(toIso(this.focusDate));
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.select(date);
            this.querySelector(`[data-date="${toIso(date)}"]`)?.focus();
        }
    };

    get startDay() {
        const attr = this.getAttribute('start-day');
        return attr !== null && attr !== '' ? Number(attr) : firstDayOfWeek(this.locale);
    }

    /* ── Rendering ────────────────────────────────────────────── */

    render() {
        const locale = this.locale;
        const startDay = this.startDay;
        const weekNumbers = this.hasAttribute('week-numbers');
        const todayIso = toIso(today());
        const focusIso = toIso(this.focusDate);

        const title = formatDate(this.month, locale, { month: 'long', year: 'numeric' });
        const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long' });
        const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: 'short' });
        const dayLabel = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

        // Grid starts on the configured first day of the week.
        const first = this.month;
        const offset = (first.getDay() - startDay + 7) % 7;
        const gridStart = addDays(first, -offset);
        const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
        const weeks = this.hasAttribute('fixed-weeks') ? 6 : Math.ceil((offset + daysInMonth) / 7);

        // Unique per instance: several calendars on a page must not share the grid's label.
        this._uid ??= this.id || `mellow-calendar-${++instances}`;
        const titleId = esc(`${this._uid}-title`);
        const selectable = this.hasAttribute('selectable-header');
        // Re-rendering replaces the dropdowns: keep focus on the one that was used.
        const activeSelect = document.activeElement?.closest?.('[data-select]')?.dataset.select;
        const min = parseIso(this.getAttribute('min'));
        const max = parseIso(this.getAttribute('max'));
        const prevDisabled = min && addDays(first, -1) < min;
        const nextDisabled = max && new Date(first.getFullYear(), first.getMonth() + 1, 1, 12) > max;

        let head = weekNumbers ? `<th scope="col" class="mellow-calendar-week"><span class="mellow-sr-only">${esc(this.t('calendar.week'))}</span><span aria-hidden="true">#</span></th>` : '';
        for (let i = 0; i < 7; i++) {
            const day = addDays(gridStart, i);
            head += `<th scope="col" abbr="${esc(weekdayLong.format(day))}">${esc(weekdayShort.format(day).replace(/\.$/, ''))}</th>`;
        }

        let body = '';
        for (let w = 0; w < weeks; w++) {
            body += '<tr>';
            if (weekNumbers) body += `<th scope="row" class="mellow-calendar-week">${isoWeek(addDays(gridStart, w * 7 + 3))}</th>`;

            for (let d = 0; d < 7; d++) {
                const date = addDays(gridStart, w * 7 + d);
                const iso = toIso(date);
                const outside = !this.isVisible(date);
                const disabled = this.isDisabled(date);
                const selected = this.isSelected(iso);
                const range = this.mode !== 'range' ? null : iso === this.selected.start ? 'start' : iso === this.selected.end ? 'end' : null;

                body += `<td role="gridcell" aria-selected="${selected}">`
                    + `<button type="button" class="mellow-calendar-day" data-date="${iso}" tabindex="${iso === focusIso ? 0 : -1}"`
                    + ` aria-label="${esc(dayLabel.format(date))}"`
                    + (iso === todayIso ? ' aria-current="date"' : '')
                    + (disabled ? ' aria-disabled="true"' : '')
                    + (outside ? ' data-outside' : '')
                    + (selected ? ' data-selected' : '')
                    + (range ? ` data-range="${range}"` : '')
                    + (this.inRange(iso) ? ' data-in-range' : '')
                    + `>${date.getDate()}</button></td>`;
            }
            body += '</tr>';
        }

        this.innerHTML = `
            <div class="mellow-calendar-header">
                <button type="button" class="mellow-calendar-nav" data-nav="-1" aria-label="${esc(this.t('calendar.previous'))}" ${prevDisabled ? 'disabled' : ''}>${CHEVRON_LEFT}</button>
                ${selectable ? this.headerSelects(locale) : ''}
                <div class="mellow-calendar-title${selectable ? ' mellow-sr-only' : ''}" id="${titleId}" aria-live="polite">${esc(title)}</div>
                <button type="button" class="mellow-calendar-nav" data-nav="1" aria-label="${esc(this.t('calendar.next'))}" ${nextDisabled ? 'disabled' : ''}>${CHEVRON_RIGHT}</button>
            </div>
            <table role="grid" class="mellow-calendar-grid" aria-labelledby="${titleId}" ${this.mode === 'multiple' || this.mode === 'range' ? 'aria-multiselectable="true"' : ''}>
                <thead><tr>${head}</tr></thead>
                <tbody>${body}</tbody>
            </table>
            ${this.hasAttribute('with-today') ? `<button type="button" class="mellow-calendar-today" data-today>${esc(this.t('calendar.today'))}</button>` : ''}
        `;

        this.querySelectorAll('select[data-select]').forEach((select) => {
            // The dropdowns' own input/change events must not reach wire:model on the calendar.
            select.addEventListener('input', (event) => event.stopPropagation());
            select.addEventListener('change', (event) => {
                event.stopPropagation();
                const month = this.querySelector('[data-select="month"]').value;
                const year = this.querySelector('[data-select="year"]').value;
                this.showMonthOf(Number(year), Number(month));
            });
        });

        if (activeSelect) this.querySelector(`[data-select="${activeSelect}"]`)?.focus();
    }
}
